<template>
  <div class="pie-donut-wrap">
    <canvas :id="id" class="chart-canvas" :height="height"></canvas>
    <div v-if="centerLabel" class="pie-donut-center">
      <span class="pie-donut-label">{{ centerLabel }}</span>
      <strong class="pie-donut-total" :style="centerFontStyle">{{ formattedTotal }}</strong>
      <span v-if="unit" class="pie-donut-unit">{{ unit }}</span>
    </div>
  </div>
</template>

<script>
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";

export default {
  name: "PieChart",
  props: {
    id: {
      type: String,
      default: "chart-pie",
    },
    height: {
      type: [String, Number],
      default: "200",
    },
    chart: {
      type: Object,
      required: true,
    },
    centerLabel: {
      type: String,
      default: "",
    },
    unit: {
      type: String,
      default: "",
    },
    isCurrency: {
      type: Boolean,
      default: false,
    },
    centerValue: {
      type: Number,
      default: null,
    },
    colorBySign: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    total() {
      if (this.centerValue !== null) return this.centerValue;
      if (!this.chart?.datasets?.data) return 0;
      return this.chart.datasets.data.reduce((a, b) => a + Number(b), 0);
    },
    formattedTotal() {
      const n = this.total;
      if (this.isCurrency) {
        return n.toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return n.toLocaleString("th-TH");
    },
    centerFontStyle() {
      const len = this.formattedTotal.replace("-", "").length;
      const fontSize =
        len <= 10 ? "22px" : len <= 12 ? "19px" : len <= 14 ? "16px" : "14px";
      const color = this.colorBySign
        ? this.total < 0 ? "#f44335" : this.total > 0 ? "#4caf50" : "#344767"
        : "#344767";
      return { fontSize, color };
    },
  },
  mounted() {
    const ctx = document.getElementById(this.id).getContext("2d");
    let chartStatus = Chart.getChart(this.id);
    if (chartStatus != undefined) chartStatus.destroy();

    const isCurrency = this.isCurrency;
    const unit = this.unit;

    new Chart(ctx, {
      type: "doughnut",
      plugins: [ChartDataLabels],
      data: {
        labels: this.chart.labels,
        datasets: [
          {
            label: this.chart.datasets.label,
            cutout: "62%",
            borderWidth: 2,
            borderColor: "#ffffff",
            backgroundColor: this.chart.datasets.backgroundColor,
            data: this.chart.datasets.data,
            fill: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (context) => {
                const total = context.dataset.data.reduce((a, b) => a + b, 0);
                const pct = total > 0 ? ((context.raw / total) * 100).toFixed(1) : "0.0";
                const valStr = isCurrency
                  ? Number(context.raw).toLocaleString("th-TH", { minimumFractionDigits: 2 })
                  : Number(context.raw).toLocaleString("th-TH");
                const unitStr = unit ? ` ${unit}` : "";
                return ` ${valStr}${unitStr}  (${pct}%)`;
              },
            },
          },
          datalabels: {
            color: "#ffffff",
            font: { weight: "bold", size: 11 },
            anchor: "center",
            align: "center",
            display: (context) => {
              const dataset = context.dataset;
              const total = dataset.data.reduce((a, b) => a + b, 0);
              return total > 0 && (dataset.data[context.dataIndex] / total) * 100 >= 5;
            },
            formatter: (value, context) => {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              return total > 0 ? ((value / total) * 100).toFixed(1) + "%" : "";
            },
          },
        },
      },
    });
  },
};
</script>

<style scoped>
.pie-donut-wrap {
  position: relative;
  width: 100%;
}

.pie-donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  pointer-events: none;
}

.pie-donut-label {
  font-size: 11px;
  font-weight: 600;
  color: #67748e;
  line-height: 1.4;
  max-width: 60%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pie-donut-total {
  color: #344767;
  line-height: 1.2;
  word-break: break-word;
  max-width: 65%;
}

.pie-donut-unit {
  font-size: 11px;
  color: #67748e;
  line-height: 1.4;
}
</style>
